import { useContext, useState, useEffect, useMemo, useRef } from 'react'
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom'

import { Typography, Box, Button, InputAdornment, TextField, RadioGroup, Radio, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButtonGroup, ToggleButton, MenuItem, Alert, Stepper, Step, StepLabel, Divider, useTheme } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import _ from 'lodash'
import { HexColorPicker } from 'react-colorful'
import isEmail from 'validator/lib/isEmail'
import isURL from 'validator/lib/isURL'

import { CopyToClipboard } from 'react-copy-to-clipboard'
import { enqueueSnackbar } from 'notistack'
import QRCode from "react-qr-code"
import BwipJs from "@bwip-js/browser"
import moment from 'moment-timezone'
import { isValidDateInput, isValidDateTimeInput } from '/utils/date-time'

import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import SaveAltOutlinedIcon from '@mui/icons-material/SaveAltOutlined'

import { AppContext } from '/providers/app-context'
import { createTestStampcard, createTestLoyaltycard, createGiftcard } from '/providers/studio-api'
import { getTemplateData } from '/providers/templates-api'
import { makePass } from '/providers/passes-api'

import PageWrapper from '/components/page-wrapper'
import TemplateSelector from '/components/templates/template-selector'
import batchSendMakePasses from '/components/make-passes/batch-send-make-passes'
import TemplateDataExportDialog from '/components/templates/template-data-export-dialog'
import parseUploadedData from '/components/make-passes/parse-uploaded-data'
import ResultsGrid from '/components/make-passes/results-grid'
import StudioTable, { useStudioTable } from '/components/studio-table'
import ActionErrorDialog from '../components/action-error-dialog'
import CreditQuote from '/components/credit-quote'
import ClickToCopy from '/components/click-to-copy'
import getLeafNodes from '/utils/get-leaf-nodes'
import ShortUniqueId from 'short-unique-id'
import currencies from '/utils/currencies.json'
import { isRequiredVariableValueForTemplate } from '/utils/variable-required'

import HelpText from '/help/template-issue-passes'

import appleButton from '/assets/images/add-to-apple-wallet.svg'
import googleButton from '/assets/images/add-to-google-wallet.svg'



const BARCODE_FORMAT_OPTIONS = ['NONE', 'QR_CODE', 'AZTEC', 'PDF_417', 'CODE_128']
const maxLivePasses = 100

const IssuePasses = () => {

  const navigate = useNavigate()
  const { loadingTemplates, templates } = useContext(AppContext)
  const { templateId } = useParams()

  const template = useMemo(() => templateId ? templates.find(t => String(t.template_id) === String(templateId)) : null, [templates, templateId])
  const pageTitle = template ? `Create Passes (${template.name})` : 'Create Passes'

  if (!loadingTemplates && !template && templateId) return <Navigate to="/templates" replace />

  const selectTemplate = selectedTemplateId => navigate(selectedTemplateId ? `/templates/${selectedTemplateId}/issue-passes` : '/issue-passes')

  if (!templateId) return <IssuePassesLayout title="Create Passes" heading="Create Passes" showTemplateSelector={false}>
    <Content template={null} templateId={templateId} onSelectTemplate={selectTemplate} />
  </IssuePassesLayout>

  if (template?.pass_type_id === "managed_stamp") return (
    <IssuePassesLayout title={pageTitle} heading={pageTitle} showTemplateSelector={false}>
      <ManagedPassFlow template={template} templateId={templateId} onSelectTemplate={selectTemplate} passType='stamp' />
    </IssuePassesLayout>
  )

  if (template?.pass_type_id === "managed_loyalty") return (
    <IssuePassesLayout title={pageTitle} heading={pageTitle} showTemplateSelector={false}>
      <ManagedPassFlow template={template} templateId={templateId} onSelectTemplate={selectTemplate} passType='loyalty' />
    </IssuePassesLayout>
  )

  if (template?.pass_type_id === "managed_gift") return (
    <IssuePassesLayout title={pageTitle} heading={pageTitle} showTemplateSelector={false}>
      <ManagedPassFlow template={template} templateId={templateId} onSelectTemplate={selectTemplate} passType='gift' />
    </IssuePassesLayout>
  )

  return (
    <IssuePassesLayout title={pageTitle} heading={pageTitle} showTemplateSelector={false}>
      <Content template={template} templateId={templateId} onSelectTemplate={selectTemplate} />
    </IssuePassesLayout>
  )
}

export default IssuePasses

const IssuePassesLayout = ({ title, heading, templateId, onSelectTemplate, showTemplateSelector = true, children }) => (
  <PageWrapper title={title} HelpText={HelpText} heading={heading}>
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {showTemplateSelector && <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}><TemplateSelector templateId={templateId} onChange={onSelectTemplate} /></Box>}
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Box>
  </PageWrapper>
)


const Content = ({ template, templateId, onSelectTemplate }) => {

  const theme = useTheme()
  const { apiSource, apiKey, processing, setProcessing } = useContext(AppContext)
  const leafNodes = useMemo(() => getLeafNodes(template?.variables ?? {}), [template?.variables])

  const [apiMode, setApiMode] = useState("test")
  const [templateData, setTemplateData] = useState(null)
  const [catalogMap, setCatalogMap] = useState({})
  const [editableIssueVars, setEditableIssueVars] = useState([])
  const [variables, setVariables] = useState({})
  const [uploadRows, setUploadRows] = useState([])
  const [uploadLimitWarning, setUploadLimitWarning] = useState('')
  const [passesToProcess, setPassesToProcess] = useState(0)
  const [makePassResults, setMakePassResults] = useState([])
  const [makePassProgress, setMakePassProgress] = useState(null)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})
  const [testResult, setTestResult] = useState(null)
  const [testResultOpen, setTestResultOpen] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [testDataSource, setTestDataSource] = useState('template')
  const [showResults, setShowResults] = useState(false)
  const [dataTemplateDialogOpen, setDataTemplateDialogOpen] = useState(false)
  const [liveRowDialog, setLiveRowDialog] = useState(null)
  const [liveRowViewMode, setLiveRowViewMode] = useState(() => {
    try {
      return window.localStorage.getItem('makePasses.liveRowViewMode') || 'list'
    } catch {
      return 'list'
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem('makePasses.liveRowViewMode', liveRowViewMode)
    } catch {
      // ignore localStorage errors
    }
  }, [liveRowViewMode])

  useEffect(() => {
    const loadTemplate = async () => {
      const result = await getTemplateData({ apiSource, apiKey, templateId: template.template_id })
      if (!result?.ok || !result?.template) return

      setTemplateData(result.template)
      setVariables(_.cloneDeep(result.template.variables ?? {}))

      const map = _.keyBy(result.template.variable_catalog ?? [], "var_name")
      setCatalogMap(map)

      // Show only variables that actually exist in this template's defaults,
      // then enrich with catalog metadata (type/required flags) when available.
      const templateLeafPaths = getLeafNodes(result.template.variables ?? {})
      const editable = _.sortBy(
        templateLeafPaths.map(varName => ({
          var_name: varName,
          var_type: map[varName]?.var_type,
          var_ui_hint: map[varName]?.var_ui_hint,
          var_formatting_sub_type: map[varName]?.var_formatting_sub_type,
          readonly_managed: map[varName]?.readonly_managed,
          allowable_in_issue_data: map[varName]?.allowable_in_issue_data,
          allowable_in_pass_data: map[varName]?.allowable_in_pass_data
        }))
          .filter(item => isIssueEditable(item, Number(result.template?.managed_pass_template) === 1)),
        ["var_name"]
      )
      setEditableIssueVars(editable)
    }

    if (template?.template_id) loadTemplate()
  }, [apiKey, apiSource, template?.template_id])

  const coerceByCatalog = input => {
    const next = _.cloneDeep(input)
    Object.keys(catalogMap).forEach(path => {
      const varType = resolveEditorValueType(catalogMap[path])
      const current = _.get(next, path)
      if (current === null || current === undefined) return

      if (varType === "boolean" && typeof current === "string") {
        const lower = current.trim().toLowerCase()
        if (lower === "true") _.set(next, path, true)
        if (lower === "false") _.set(next, path, false)
      }

      if (["integer", "number", "money"].includes(varType) && typeof current === "string") {
        const trimmed = current.trim()
        const isValidInteger = /^-?\d+$/.test(trimmed)
        const isValidNumber = trimmed !== "" && Number.isFinite(Number(trimmed))
        if ((varType === "integer" && isValidInteger) || (varType !== "integer" && isValidNumber)) {
          _.set(next, path, Number(trimmed))
        }
      }
    })
    return next
  }

  const setFieldValue = (path, nextValue) => setVariables(current => {
    const next = _.cloneDeep(current)
    _.set(next, path, nextValue)
    return next
  })

  const handleTestUpload = async file => {
    const reader = new FileReader()
    reader.onloadend = () => {
      try {
        const base64Data = reader.result.replace(/[^,]+base64,/, '')
        const parsedData = parseUploadedData({ base64Data, file, variables: templateData?.variables ?? template.variables })
        const firstRow = Array.isArray(parsedData) ? parsedData[0] : null
        if (!firstRow) return enqueueSnackbar({ message: 'Uploaded file is empty', variant: 'warning' })
        setUploadLimitWarning(Array.isArray(parsedData) && parsedData.length > 1
          ? 'Test mode creates one pass per request. Only the first uploaded row will be used.'
          : '')
        const uploadedRow = coerceByCatalog(firstRow)
        setUploadRows([uploadedRow])
        setTestDataSource('upload')
        setVariables(coerceByCatalog(_.merge({}, _.cloneDeep(templateData?.variables ?? template.variables), uploadedRow)))
        enqueueSnackbar({ message: 'Variables loaded from upload', variant: 'success' })
      }
      catch (err) {
        enqueueSnackbar({ message: err?.message ?? 'Failed to parse file', variant: 'error' })
      }
    }
    reader.readAsDataURL(file)
  }

  const handleLiveUpload = async file => {
    const reader = new FileReader()
    reader.onloadend = () => {
      try {
        const base64Data = reader.result.replace(/[^,]+base64,/, '')
        const parsedData = parseUploadedData({
          base64Data,
          file,
          variables: templateData?.variables ?? template.variables,
          requireJsonArray: file.name.toLowerCase().endsWith('.json')
        }) ?? []

        if (!Array.isArray(parsedData) || !parsedData.length) {
          return enqueueSnackbar({ message: 'Upload must contain at least one row', variant: 'warning' })
        }

        if (parsedData.length > maxLivePasses) {
          setUploadRows([])
          setUploadLimitWarning(`Live uploads are limited to ${maxLivePasses} passes. Please upload a smaller file.`)
          return enqueueSnackbar({ message: `Live uploads are limited to ${maxLivePasses} passes`, variant: 'warning' })
        }

        setUploadLimitWarning('')
        setUploadRows(parsedData.map(row => coerceByCatalog(row)))
        enqueueSnackbar({ message: `Loaded ${parsedData.length} row(s)`, variant: 'success' })
      }
      catch (err) {
        enqueueSnackbar({ message: err?.message ?? 'Failed to parse upload', variant: 'error' })
      }
    }
    reader.readAsDataURL(file)
  }

  const createSingleTestPass = async () => {
    setProcessing(true)
    try {
      const payload = buildIssueVariablesPayload({
        source: coerceByCatalog(variables),
        templateDefaults: templateData?.variables ?? template.variables ?? {},
        catalogMap,
        isManagedPassTemplate: Number(templateData?.managed_pass_template) === 1,
        includeDuplicates: false
      })
      const result = await makePass({
        apiKey,
        apiSource,
        apiMode: "test",
        templateId: template.template_id,
        variables: payload
      })

      if (!result?.ok) {
        setMakePassError(result?.error ?? { message: "Failed to create pass" })
        setErrorDialogOpen(true)
        return
      }

      setTestResult({
        id: result.pass_id,
        installUrl: result.shortUrl ?? result.short_url,
        appleUrl: result.apple_url ?? result.appleUrl,
        googleUrl: result.google_url ?? result.googleUrl,
      })
      setMakePassResults([{
        id: result.pass_id,
        installUrl: result.shortUrl ?? result.short_url,
        appleUrl: result.apple_url ?? result.appleUrl,
        googleUrl: result.google_url ?? result.googleUrl,
      }])
      setPassesToProcess(1)
      setActiveStep(5)
      setShowResults(true)
    }
    finally {
      setProcessing(false)
    }
  }

  const createLivePasses = async () => {
    const templateDefaults = templateData?.variables ?? template.variables ?? {}
    const isManual = testDataSource === 'manual'
    const passData = isManual
      ? [buildIssueVariablesPayload({
          source: coerceByCatalog(variables),
          templateDefaults,
          catalogMap,
          isManagedPassTemplate: Number(templateData?.managed_pass_template) === 1,
          includeDuplicates: false
        })]
      : uploadRows.map(row => ({
          ...buildIssueVariablesPayload({
            source: coerceByCatalog(row),
            templateDefaults,
            catalogMap,
            isManagedPassTemplate: Number(templateData?.managed_pass_template) === 1,
            includeDuplicates: false
          }),
          _mailmerge: row._mailmerge
        }))

    if (!passData.length) return

    setProcessing(true)
    setMakePassResults([])
    setPassesToProcess(0)
    setMakePassProgress(null)

    try {
      const response = await batchSendMakePasses({
        apiKey,
        apiSource,
        apiMode: "live",
        passData,
        templateId: template.template_id,
        setPassesToProcess,
        setMakePassResults,
        batchSize: 10,
        onProgress: setMakePassProgress
      })

      if (!response?.ok) {
        setMakePassError(response?.error ?? { message: 'Failed to create passes' })
        setErrorDialogOpen(true)
        return
      }

      setActiveStep(5)
      setShowResults(true)
    }
    finally {
      setProcessing(false)
    }
  }

  const uploadedPaths = useMemo(() => _.uniq(
    uploadRows.flatMap(row => getLeafNodes(row).filter(path => _.has(row, path)))
  ), [uploadRows])

  const getUploadCellValidation = (row, path) => {
    if (!_.has(row, path)) return { valid: true, state: 'missing' }

    const value = _.get(row, path)
    const catalogEntry = catalogMap[path]
    const isManaged = Number(templateData?.managed_pass_template) === 1

    if (path === 'pass.pass_id') return { valid: false, state: 'unexpected', message: 'pass.pass_id is not permitted.' }
    if (path === '_mailmerge.name') return { valid: true, state: 'accepted' }
    if (path === '_mailmerge.email') {
      if (value === null || value === undefined || String(value).trim() === '') return { valid: true, state: 'accepted' }
      const valid = isEmail(String(value).trim())
      return valid
        ? { valid: true, state: 'accepted' }
        : { valid: false, state: 'invalid', message: '_mailmerge.email must be a valid email address.' }
    }
    if (!leafNodes.includes(path)) return { valid: false, state: 'unexpected', message: 'Variable is not defined by the template.' }
    if (Number(catalogEntry?.allowable_in_pass_data) !== 1) return { valid: false, state: 'unexpected', message: `${path} is template-only and is not permitted in uploaded data.` }
    if (isManaged && Number(catalogEntry?.readonly_managed) === 1 && Number(catalogEntry?.allowable_in_issue_data) !== 1) return { valid: false, state: 'unexpected', message: `${path} is read-only and is not permitted in issue data.` }

    const validation = validateValueInput({
      input: value,
      varSubType: resolveEditorValueType(catalogEntry),
      required: isRequiredVariableValueForTemplate({ catalogEntry, isManagedPassTemplate: isManaged }),
      label: path
    })

    if (!validation.valid) return { valid: false, state: 'invalid', message: validation.helperText }
    if (_.has(templateData?.variables ?? template?.variables, path) && _.isEqual(value, _.get(templateData?.variables ?? template?.variables, path))) {
      return { valid: true, state: 'duplicate', message: 'Matches template default.' }
    }
    return { valid: true, state: 'accepted' }
  }

  const uploadValidation = useMemo(() => uploadRows.map(row => {
    const paths = getLeafNodes(row).filter(path => _.has(row, path))
    const errors = paths
      .map(path => getUploadCellValidation(row, path))
      .filter(validation => !validation.valid)
    return { valid: paths.length > 0 && errors.length === 0, errors }
  }), [catalogMap, leafNodes, template?.variables, templateData?.managed_pass_template, templateData?.variables, uploadRows])

  const hasUploadValidationErrors = uploadValidation.some(validation => !validation.valid)
  const uploadValidationMessages = useMemo(() => _.uniq(
    uploadValidation.flatMap(validation => validation.errors.map(error => error.message).filter(Boolean))
  ), [uploadValidation])
  const reviewingUploadedData = testDataSource === 'upload' || (apiMode === 'live' && testDataSource === 'upload')

  const manualValidationErrors = useMemo(() => editableIssueVars
    .map(item => validateValueInput({
      input: _.get(variables, item.var_name),
      varSubType: resolveEditorValueType(item),
      required: isRequiredVariableValueForTemplate({
        catalogEntry: item,
        isManagedPassTemplate: Number(templateData?.managed_pass_template) === 1
      }),
      label: item.var_name
    }))
    .filter(validation => !validation.valid),
  [editableIssueVars, templateData?.managed_pass_template, variables])

  const livePreviewRows = useMemo(() => uploadRows.map((row, idx) => ({ __row_id: idx + 1, ...row })), [uploadRows])

  const liveColumns = useMemo(() => {
    return [
      { accessorKey: '__row_id', header: '#', size: 70 },
      {
        id: 'view',
        header: 'View',
        size: 100,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Button
            size='small'
            startIcon={<VisibilityOutlinedIcon />}
            onClick={event => {
              event.stopPropagation()
              setLiveRowDialog(uploadRows[row.index])
            }}
          >
            View
          </Button>
        )
      },
      ...uploadedPaths.map(path => ({
        id: path,
        accessorFn: row => _.get(row, path),
        header: path,
        size: 220,
        Cell: ({ cell, row }) => {
          const validation = getUploadCellValidation(row.original, path)
          const value = cell.getValue()
          return <span
            title={validation.message}
            style={{
              color: validation.state === 'invalid'
                ? theme.palette.error.main
                : validation.state === 'unexpected'
                  ? theme.palette.error.main
                : validation.state === 'duplicate'
                  ? theme.palette.warning.main
                  : theme.palette.success.main
            }}
          >{value === null || value === undefined ? '' : value.toString()}</span>
        }
      }))
    ]
  }, [catalogMap, leafNodes, template?.variables, templateData?.managed_pass_template, templateData?.variables, theme.palette.error.main, theme.palette.info.main, theme.palette.success.main, theme.palette.warning.main, uploadedPaths, uploadRows])

  const liveTable = useStudioTable({
    tableKey: 'issue-passes-live',
    columns: liveColumns,
    data: livePreviewRows,
    enablePagination: true,
    enableRowSelection: false,
    enableDensityToggle: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    positionToolbarAlertBanner: 'none',
    muiTableProps: { sx: { backgroundColor: 'transparent !important' } },
    muiTableBodyProps: { sx: { backgroundColor: 'transparent !important' } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => setLiveRowDialog(uploadRows[row.index]),
      sx: { cursor: 'pointer', backgroundColor: 'transparent !important', '&:hover': { backgroundColor: 'transparent !important' } }
    }),
  })

  const duplicateSummary = useMemo(() => {
    const templateDefaults = templateData?.variables ?? template?.variables ?? {}
    const isManaged = Number(templateData?.managed_pass_template) === 1
    const paths = _.uniq([...getLeafNodes(templateDefaults), ...uploadRows.flatMap(row => getLeafNodes(row))])

    let rowsWithDuplicates = 0
    let totalDuplicateValues = 0
    uploadRows.forEach(row => {
      let rowHasDuplicate = false
      paths.forEach(path => {
        if (!_.has(row, path)) return
        const required = isRequiredVariableValueForTemplate({ catalogEntry: catalogMap[path], isManagedPassTemplate: isManaged })
        if (required) return
        if (_.has(templateDefaults, path) && _.isEqual(_.get(row, path), _.get(templateDefaults, path))) {
          totalDuplicateValues += 1
          rowHasDuplicate = true
        }
      })
      if (rowHasDuplicate) rowsWithDuplicates += 1
    })

    return { rowsWithDuplicates, totalDuplicateValues }
  }, [catalogMap, template?.variables, templateData?.managed_pass_template, templateData?.variables, uploadRows])

  useEffect(() => {
    if (!makePassResults.length) setShowResults(false)
  }, [makePassResults.length])
  const issueMorePasses = () => {
    setMakePassResults([])
    setMakePassProgress(null)
    setPassesToProcess(0)
    setUploadRows([])
    setUploadLimitWarning('')
    setApiMode("test")
    setTestDataSource("template")
    setVariables(_.cloneDeep(templateData?.variables ?? template?.variables ?? {}))
    setActiveStep(0)
    setShowResults(false)
    setProcessing(false)
  }

  const discardLiveUpload = () => {
    setUploadRows([])
    setUploadLimitWarning('')
    setTestDataSource(apiMode === 'test' ? 'template' : 'manual')
    setVariables(_.cloneDeep(templateData?.variables ?? template?.variables ?? {}))
    setActiveStep(2)
  }

  if (showResults || (apiMode === "live" && processing)) {
    return (
      <div className='h-full flex flex-col min-h-0'>
        <Stepper activeStep={showResults ? 5 : 4} alternativeLabel sx={{ mb: 4, px: 4, pt: 3 }}>
          {['Choose template', 'Pass mode', 'Upload data', 'Pass values', 'Confirm', 'Results'].map((label, index) => (
            <Step
              key={label}
              onClick={() => {
                if (showResults && index < 5) {
                  setShowResults(false)
                  setActiveStep(index)
                }
              }}
              sx={{ cursor: showResults && index < 5 ? 'pointer' : 'default' }}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <ResultsGrid
          template={template}
          passesToProcess={passesToProcess}
          makePassResults={makePassResults}
          onIssueMore={issueMorePasses}
          progress={makePassProgress}
        />
        <ActionErrorDialog title="Create passes error" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
      </div>
    )
  }

  return (
    <>
      <div className='h-full min-h-0 w-full mt-3 overflow-y-auto px-4 pb-4 flex flex-col'>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, px: 4, pt: 3 }}>
          {['Choose template', 'Pass mode', 'Upload data', 'Pass values', 'Confirm', 'Results'].map((label, index) => (
            <Step
              key={label}
              onClick={() => index < activeStep && setActiveStep(index)}
              sx={{ cursor: index < activeStep ? 'pointer' : 'default' }}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && <StepContent title="Select template">
          <TemplateSelector templateId={templateId} onChange={onSelectTemplate} fullWidth />
          <StepActions onBack={() => {}} onNext={() => setActiveStep(1)} nextDisabled={!template} backDisabled />
        </StepContent>}

        {activeStep === 1 && <StepContent title="Choose pass mode">
          <Typography sx={{ mb: 2 }}>Choose whether to create a test pass or live passes.</Typography>
          <RadioGroup value={apiMode} onChange={event => {
            const mode = event.target.value
            setApiMode(mode)
            setTestDataSource(mode === 'test' ? 'template' : 'manual')
            setUploadRows([])
            setUploadLimitWarning('')
            setVariables(_.cloneDeep(templateData?.variables ?? template.variables ?? {}))
          }}>
            <FormControlLabel value="test" control={<Radio />} label="Create a TEST pass" />
            <FormControlLabel value="live" control={<Radio />} label="Create LIVE passes" />
          </RadioGroup>
          <StepActions onBack={() => setActiveStep(0)} onNext={() => setActiveStep(2)} />
        </StepContent>}

        {activeStep === 2 && <StepContent title="Choose pass data">
          <div className='mb-3'>
            <Button variant='outlined' onClick={() => setDataTemplateDialogOpen(true)}>
              <SaveAltOutlinedIcon className='mr-2' />Download data template
            </Button>
          </div>
          <Typography sx={{ mb: 2 }}>
            {apiMode === 'test'
              ? 'Choose how to create the test pass. Only one test pass can be created at a time.'
              : 'Choose whether to create one live pass with custom values or upload data for 1–100 live passes.'}
          </Typography>
          <RadioGroup value={testDataSource} onChange={event => {
            const source = event.target.value
            setTestDataSource(source)
            setUploadRows([])
            setUploadLimitWarning('')
            if (source === 'template' || source === 'manual') {
              setVariables(_.cloneDeep(templateData?.variables ?? template.variables ?? {}))
            }
          }}>
            {apiMode === 'test' && <FormControlLabel value="template" control={<Radio />} label="Proceed using default template data as pass data" />}
            <FormControlLabel value="manual" control={<Radio />} label={`Enter pass data in a form ${(apiMode === 'live' ? "1 pass" : "")}`} />
            <FormControlLabel value="upload" control={<Radio />} label={`Upload a data file ${apiMode === 'live' ? "(1 to 100 passes)" : ""}`} />
          </RadioGroup>
          {testDataSource === 'upload' && <div className='mt-3'>
            <Typography sx={{ mb: 2 }}>{apiMode === 'test' ? 'The first row will be used to create one test pass.' : 'Upload a file containing 1–100 passes.'}</Typography>
            <UploadDataFileInput onFile={apiMode === 'test' ? handleTestUpload : handleLiveUpload} />
            {uploadLimitWarning && <Alert severity="warning" sx={{ mt: 2 }}>{uploadLimitWarning}</Alert>}
            {uploadRows.length > 0 && <Alert severity="success" sx={{ mt: 2 }}>{uploadRows.length} row(s) loaded.</Alert>}
          </div>}
          <StepActions onBack={() => setActiveStep(1)} onNext={() => setActiveStep(3)} nextDisabled={testDataSource === 'upload' && !uploadRows.length} />
        </StepContent>}

        {activeStep === 3 && <StepContent title={reviewingUploadedData ? 'Review uploaded data' : 'Choose pass values'} fill={reviewingUploadedData}>
          {!reviewingUploadedData && testDataSource === 'manual' ? <>
            <Typography sx={{ mb: 2 }}>Only pass variables can be overridden.</Typography>
            <div className='flex flex-col gap-2'>
              {editableIssueVars.map(item => <div key={item.var_name} className='grid grid-cols-[minmax(260px,1fr)_minmax(420px,2fr)] gap-3 items-center'>
                <div className='font-semibold'>{item.var_name}</div>
                <VariableInput
                  value={_.get(variables, item.var_name)}
                  templateDefaultValue={_.get(templateData?.variables ?? template.variables, item.var_name)}
                  varType={resolveEditorValueType(item)}
                  required={isRequiredVariableValueForTemplate({ catalogEntry: item, isManagedPassTemplate: Number(templateData?.managed_pass_template) === 1 })}
                  label={item.var_name}
                  onChange={value => setFieldValue(item.var_name, value)}
                />
              </div>)}
              {!editableIssueVars.length && <div>No variables found in this template.</div>}
            </div>
            {manualValidationErrors.length > 0 && <Alert severity='error'>Complete the required pass values and correct any invalid values before continuing.</Alert>}
          </> : <>
            {reviewingUploadedData ? <Typography>Review the uploaded rows before continuing.</Typography> : <Typography>Template default values will be used to create one pass.</Typography>}
            {reviewingUploadedData && duplicateSummary.totalDuplicateValues > 0 && <Alert severity='warning' sx={{ mb: 2 }}>
              Found <b>{duplicateSummary.totalDuplicateValues}</b> uploaded values across <b>{duplicateSummary.rowsWithDuplicates}</b> row(s) match template defaults. These values will be used as uploaded; re-upload corrected data if necessary.
            </Alert>}
            {reviewingUploadedData && uploadValidationMessages.length > 0 && <Alert severity='error' sx={{ mb: 2 }}>
              <div className='font-semibold'>Uploaded data contains values that cannot be used.</div>
              <ul className='list-disc pl-5'>
                {uploadValidationMessages.map(message => <li key={message}>{message}</li>)}
              </ul>
            </Alert>}
            {reviewingUploadedData && <div className='flex-1 min-h-0 overflow-hidden'><StudioTable table={liveTable} /></div>}
          </>}
          <StepActions
            onBack={() => setActiveStep(2)}
            onNext={() => setActiveStep(4)}
            backContent={reviewingUploadedData ? <Button variant='outlined' onClick={discardLiveUpload}>Discard upload</Button> : null}
            nextDisabled={(reviewingUploadedData && hasUploadValidationErrors) || (testDataSource === 'manual' && manualValidationErrors.length > 0)}
          />
        </StepContent>}

        {activeStep === 4 && <StepContent title="Confirm pass creation">
          <PassConfirmation
            template={template}
            apiMode={apiMode}
            passesToCreate={apiMode === 'test' ? 1 : testDataSource === 'manual' ? 1 : uploadRows.length}
            values={apiMode === 'test' ? (testDataSource === 'template' ? 'Template defaults' : testDataSource === 'upload' ? 'Uploaded data' : 'Manual values') : undefined}
            onBack={() => setActiveStep(3)}
            onNext={apiMode === 'test' ? createSingleTestPass : createLivePasses}
            nextLabel={apiMode === 'test' ? 'Create TEST pass' : 'Create LIVE passes'}
            nextDisabled={processing || (apiMode === 'live' && ((testDataSource === 'upload' && (!uploadRows.length || hasUploadValidationErrors)) || (testDataSource === 'manual' && manualValidationErrors.length > 0)))}
          />
        </StepContent>}
      </div>

      <Dialog
        open={testResultOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return
          setTestResultOpen(false)
        }}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Pass created</DialogTitle>
        <DialogContent>
          <div className='mb-4'>
            Pass ID: <ClickToCopy source={testResult?.id}>{testResult?.id}</ClickToCopy>
          </div>
          <div className='flex flex-row gap-5 justify-between'>
            <InstallPanel url={testResult?.appleUrl} buttonImage={appleButton} label='Apple URL' />
            <InstallPanel url={testResult?.googleUrl} buttonImage={googleButton} label='Google URL' />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={() => setTestResultOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <TemplateDataExportDialog open={dataTemplateDialogOpen} setOpen={setDataTemplateDialogOpen} template={templateData ?? template} />

      <Dialog open={Boolean(liveRowDialog)} onClose={() => setLiveRowDialog(null)} maxWidth='md' fullWidth>
        <DialogTitle>Uploaded row</DialogTitle>
        <DialogContent>
          <div className='flex justify-end mb-2'>
            <ToggleButtonGroup size='small' value={liveRowViewMode} exclusive onChange={(_, mode) => mode && setLiveRowViewMode(mode)}>
              <ToggleButton value='list'>List</ToggleButton>
              <ToggleButton value='json'>JSON</ToggleButton>
            </ToggleButtonGroup>
          </div>
          {liveRowViewMode === 'json' ? (
            <pre className='p-3 rounded bg-black/20 overflow-auto max-h-[50vh]'>{JSON.stringify(liveRowDialog, null, 2)}</pre>
          ) : (
            <div className='grid grid-cols-[minmax(260px,1fr)_minmax(260px,2fr)] gap-2'>
              {uploadedPaths.map(path => (
                <div key={path} className='contents'>
                  <div className='font-semibold'>{path}</div>
                  <div>{String(_.get(liveRowDialog, path) ?? "")}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setLiveRowDialog(null)} startIcon={<CloseIcon />}>Close</Button>
        </DialogActions>
      </Dialog>

      <ActionErrorDialog title="Create passes error" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
    </>
  )
}

const StepContent = ({ title, children, fill = false }) => (
  <div className='flex-1 min-h-0 w-full py-2 flex flex-col'>
    <Typography variant='h5' sx={{ mb: 3 }}>{title}</Typography>
    <div className='flex flex-col gap-4 flex-1 min-h-0'>{children}</div>
  </div>
)

const StepActions = ({ onBack, onNext, nextLabel = 'Continue', nextDisabled = false, backDisabled = false, backContent }) => (
  <div className='flex justify-between mt-auto pt-8 pb-2 flex-shrink-0'>
    <div className='flex gap-3'>
      <Button variant='outlined' onClick={onBack} disabled={backDisabled}>Back</Button>
      {backContent}
    </div>
    <Button variant='contained' onClick={onNext} disabled={nextDisabled}>{nextLabel}</Button>
  </div>
)

const PassConfirmation = ({ template, apiMode, passesToCreate, values, onBack, onNext, nextLabel, nextDisabled = false, children }) => {
  const [creditQuote, setCreditQuote] = useState(null)
  const insufficientCredits = apiMode === 'live' && creditQuote?.ok && !creditQuote.sufficient

  return <>
    <Typography>Review the request before creating {passesToCreate === 1 ? 'the pass' : 'passes'}.</Typography>
    <Divider sx={{ my: 3 }} />
    <div className='flex flex-col gap-2'>
      <div><b>Template:</b> {template?.name}</div>
      <div><b>Mode:</b> {apiMode === 'test' ? 'TEST' : 'LIVE'}</div>
      <div><b>Passes to create:</b> {passesToCreate}</div>
      {values && <div><b>Values:</b> {values}</div>}
    </div>
    {apiMode === 'live' && <CreditQuote templateId={template?.template_id} eventType='issue' passCount={passesToCreate} mode={apiMode} onChange={setCreditQuote} />}
    {children}
    <StepActions onBack={onBack} onNext={onNext} nextLabel={nextLabel} nextDisabled={nextDisabled || insufficientCredits || (apiMode === 'live' && !creditQuote)} />
  </>
}

const isIntegerInput = input => /^-?\d*$/.test(input)
const isDecimalInput = input => /^-?\d*(\.\d*)?$/.test(input)
const isValidColourInput = input => /^#?[0-9a-fA-F]{6}$/.test(input ?? "")
const normaliseColourInput = input => /^#[0-9a-fA-F]{6}$/.test(input ?? "")
  ? input
  : /^[0-9a-fA-F]{6}$/.test(input ?? "")
    ? `#${input}`
    : null
const isValidCurrencyCodeInput = input => /^[A-Z]{3}$/.test(input ?? "")
  && currencies.some(currency => currency.code === input)
const isColourSubType = varSubType => ["colour", "color", "colours", "colors"].includes(varSubType)
const resolveEditorValueType = meta => meta?.var_formatting_sub_type ?? meta?.var_ui_hint ?? meta?.var_type ?? "text"

const validateValueInput = ({ input, varSubType, required = false, label = "Value" }) => {
  const value = (input ?? "").toString()
  if (required && value.trim() === "") return { valid: false, helperText: `${label} is required.` }
  if (!required && value.trim() === "" && !isColourSubType(varSubType)) return { valid: true, helperText: "" }

  switch (varSubType) {
    case "boolean":
      return {
        valid: typeof input === "boolean" || value.trim().toLowerCase() === "true" || value.trim().toLowerCase() === "false",
        helperText: typeof input === "boolean" || ['true', 'false'].includes(value.trim().toLowerCase()) ? "" : "Use true or false."
      }
    case "email":
      return { valid: isEmail(value), helperText: isEmail(value) ? "" : "Enter a valid email address." }
    case "www":
    case "link":
      return {
        valid: isURL(value, { protocols: ['http', 'https'], require_protocol: true }),
        helperText: isURL(value, { protocols: ['http', 'https'], require_protocol: true }) ? "" : "Enter a valid URL starting with http:// or https://."
      }
    case "number":
    case "money":
      return { valid: isDecimalInput(value), helperText: isDecimalInput(value) ? "" : "Enter a valid number." }
    case "integer":
      return { valid: isIntegerInput(value), helperText: isIntegerInput(value) ? "" : "Enter a valid integer." }
    case "date":
      return { valid: isValidDateInput(value), helperText: isValidDateInput(value) ? "" : "Use ISO format YYYY-MM-DD and enter a valid date." }
    case "datetime":
      return { valid: isValidDateTimeInput(value), helperText: isValidDateTimeInput(value) ? "" : "Use ISO format YYYY-MM-DDTHH:mm:ss±HH:MM and a valid time." }
    case "colour":
    case "color":
    case "colours":
    case "colors":
      return { valid: isValidColourInput(value), helperText: isValidColourInput(value) ? "" : "Use a 6-digit hex colour such as #A1B2C3." }
    case "choice":
      return { valid: isValidCurrencyCodeInput(value), helperText: isValidCurrencyCodeInput(value) ? "" : "Use a valid 3-letter ISO currency code such as GBP." }
    case "barcode_format":
      return {
        valid: BARCODE_FORMAT_OPTIONS.includes(value),
        helperText: BARCODE_FORMAT_OPTIONS.includes(value) ? "" : `Choose one of: ${BARCODE_FORMAT_OPTIONS.join(", ")}.`
      }
    default:
      return { valid: true, helperText: "" }
  }
}

const isIssueEditable = (item, isManagedPassTemplate = false) => {
  if (!item) return true
  if (Number(item.allowable_in_pass_data) !== 1) return false
  if (!isManagedPassTemplate) return true
  return Number(item.readonly_managed) !== 1 || Number(item.allowable_in_issue_data) === 1
}

const VariableInput = ({ value, templateDefaultValue, varType, onChange, required = false, label = "Value" }) => {
  const stringValue = value?.toString?.() ?? ""
  const validation = validateValueInput({ input: stringValue, varSubType: varType, required, label })
  const matchesTemplateDefault = _.isEqual(value, templateDefaultValue)
  const helperText = !validation.valid
    ? validation.helperText
    : matchesTemplateDefault
      ? "Matches template default (will not be overridden)."
      : ""
  const defaultValueFieldSx = matchesTemplateDefault
    ? { "& .MuiInputBase-root": { opacity: 0.75, backgroundColor: "action.hover" } }
    : undefined

  if (varType === "boolean") {
    return (
      <TextField
        select
        size='small'
        fullWidth
        value={stringValue === "" ? "false" : stringValue.toLowerCase()}
        onChange={event => onChange(event.target.value === "true")}
        error={!validation.valid}
        helperText={helperText}
        sx={defaultValueFieldSx}
      >
        <MenuItem value="false">false</MenuItem>
        <MenuItem value="true">true</MenuItem>
      </TextField>
    )
  }

  if (isColourSubType(varType)) {
    const pickerColour = normaliseColourInput(stringValue) ?? "#ffffff"

    return (
      <div className='flex flex-col gap-2'>
        <TextField
          size='small'
          fullWidth
          value={stringValue}
          onChange={event => onChange(event.target.value)}
          error={!validation.valid}
          helperText={helperText}
          sx={defaultValueFieldSx}
        />
        <HexColorPicker color={pickerColour} onChange={onChange} />
      </div>
    )
  }

  if (varType === "choice") {
    return (
      <TextField
        select
        size='small'
        fullWidth
        value={stringValue}
        onChange={event => onChange(event.target.value)}
        error={!validation.valid}
        helperText={helperText}
        sx={defaultValueFieldSx}
      >
        <MenuItem value="">Select currency</MenuItem>
        {currencies.map(currency => (
          <MenuItem key={currency.code} value={currency.code}>
            {currency.code} - {currency.name}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  if (varType === "barcode_format") {
    return (
      <TextField
        select
        size='small'
        fullWidth
        value={stringValue}
        onChange={event => onChange(event.target.value)}
        error={!validation.valid}
        helperText={helperText}
        sx={defaultValueFieldSx}
      >
        {BARCODE_FORMAT_OPTIONS.map(option => (
          <MenuItem key={option} value={option}>{option}</MenuItem>
        ))}
      </TextField>
    )
  }

  if (["integer", "number", "money"].includes(varType)) {
    return (
      <TextField
        size='small'
        fullWidth
        value={stringValue}
        onChange={event => {
          if (varType === "integer" && !isIntegerInput(event.target.value)) return
          if (["number", "money"].includes(varType) && !isDecimalInput(event.target.value)) return
          onChange(event.target.value)
        }}
        inputProps={{ inputMode: 'decimal' }}
        error={!validation.valid}
        helperText={helperText}
        sx={defaultValueFieldSx}
      />
    )
  }

  return (
    <TextField
      size='small'
      fullWidth
      value={stringValue}
      onChange={event => onChange(event.target.value)}
      error={!validation.valid}
      helperText={helperText}
      sx={defaultValueFieldSx}
    />
  )
}

const unsetPathAndPrune = (obj, path) => {
  _.unset(obj, path)
  const parts = path.split(".")
  for (let i = parts.length - 1; i > 0; i -= 1) {
    const parentPath = parts.slice(0, i).join(".")
    const parentValue = _.get(obj, parentPath)
    if (_.isPlainObject(parentValue) && Object.keys(parentValue).length === 0) _.unset(obj, parentPath)
    else break
  }
}

const buildIssueVariablesPayload = ({ source, templateDefaults, catalogMap, isManagedPassTemplate, includeDuplicates = false }) => {
  const next = {}
  const sourcePaths = getLeafNodes(source ?? {})
  const allowedPaths = sourcePaths.filter(path => {
    const catalogEntry = catalogMap?.[path]
    if (!catalogEntry || Number(catalogEntry.allowable_in_pass_data) !== 1) return false
    if (isManagedPassTemplate && Number(catalogEntry.readonly_managed) === 1 && Number(catalogEntry.allowable_in_issue_data) !== 1) return false
    return true
  })
  allowedPaths.forEach(path => _.set(next, path, _.get(source, path)))
  const paths = _.uniq([...getLeafNodes(templateDefaults ?? {}), ...allowedPaths])

  paths.forEach(path => {
    if (!_.has(next, path)) return
    const sameAsDefault = _.has(templateDefaults, path) && _.isEqual(_.get(next, path), _.get(templateDefaults, path))

    if (!includeDuplicates && sameAsDefault) {
      unsetPathAndPrune(next, path)
    }
  })

  return next
}

const ManagedPassFlow = ({ template, templateId, onSelectTemplate, passType }) => {
  const { apiSource, apiKey, processing, setProcessing } = useContext(AppContext)
  const [apiMode, setApiMode] = useState('test')
  const [activeStep, setActiveStep] = useState(0)
  const [createdResult, setCreatedResult] = useState(null)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})

  const handleCreated = result => {
    setCreatedResult({
      id: result.pass_id,
      installUrl: result.shortUrl ?? result.short_url,
      appleUrl: result.apple_url ?? result.appleUrl,
      googleUrl: result.google_url ?? result.googleUrl,
    })
    setActiveStep(5)
  }

  const issueMorePasses = () => {
    setCreatedResult(null)
    setActiveStep(1)
  }

  const createManagedTestPass = async () => {
    setProcessing(true)
    const result = passType === 'stamp'
      ? await createTestStampcard({ apiSource, apiKey, apiMode: 'test', templateId: template.template_id })
      : passType === 'loyalty'
        ? await createTestLoyaltycard({ apiSource, apiKey, apiMode: 'test', templateId: template.template_id })
        : await createGiftcard({ apiSource, apiKey, apiMode: 'test', templateId: template.template_id })
    setProcessing(false)

    if (result.ok === false) {
      setMakePassError(result.error)
      setErrorDialogOpen(true)
      return
    }

    handleCreated(result)
  }

  const openManagedSignup = () => {
    const typePath = { stamp: 'stamp', loyalty: 'loyalty', gift: 'gift' }[passType]
    if (!typePath) return
    window.open(`https://signup.smartix.uk/${typePath}/${template.client_id}/${template.template_id}`, '_blank', 'noopener')
    setActiveStep(0)
  }

  return (
    <div className='h-full min-h-0 w-full mt-3 overflow-y-auto px-4 pb-4 flex flex-col'>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, px: 4, pt: 3 }}>
        {['Choose template', 'Pass mode', 'Upload data', 'Pass values', 'Confirm', 'Results'].map((label, index) => (
          <Step
            key={label}
            disabled={index === 2 || index === 3}
            completed={index < activeStep && index !== 2 && index !== 3}
            onClick={() => index < activeStep && index !== 2 && index !== 3 && setActiveStep(index)}
            sx={{ cursor: index < activeStep && index !== 2 && index !== 3 ? 'pointer' : 'default' }}
          >
            <StepLabel disabled={index === 2 || index === 3}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && <StepContent title='Select template'>
        <TemplateSelector templateId={templateId} onChange={onSelectTemplate} fullWidth />
        <StepActions onBack={() => {}} onNext={() => setActiveStep(1)} nextDisabled={!template} backDisabled />
      </StepContent>}

      {activeStep === 1 && <StepContent title='Choose pass mode'>
        <RadioGroup row value={apiMode} onChange={event => setApiMode(event.target.value)}>
          <FormControlLabel value='test' control={<Radio />} label='Test pass' />
          <FormControlLabel value='live' control={<Radio />} label='Live pass' />
        </RadioGroup>
        <StepActions onBack={() => setActiveStep(0)} onNext={() => setActiveStep(4)} />
      </StepContent>}

      {activeStep === 4 && <StepContent title='Confirm pass creation'>
        <PassConfirmation
          template={template}
          apiMode={apiMode}
          passesToCreate={1}
          onBack={() => setActiveStep(1)}
          onNext={apiMode === 'test' ? createManagedTestPass : openManagedSignup}
          nextLabel={apiMode === 'test' ? 'Create TEST pass' : 'Continue'}
          nextDisabled={processing}
        >
          {apiMode === 'live' && <div className='mt-4'>
            {passType === 'stamp' && <StampcardSignup template={template} mode={apiMode} onCreated={handleCreated} />}
            {passType === 'loyalty' && <LoyaltycardSignup template={template} mode={apiMode} onCreated={handleCreated} />}
            {passType === 'gift' && <Giftcard template={template} mode={apiMode} onCreated={handleCreated} />}
          </div>}
        </PassConfirmation>
      </StepContent>}

      {activeStep === 5 && <StepContent title='Results' fill>
        {createdResult && <ResultsGrid template={template} passesToProcess={1} makePassResults={[createdResult]} onIssueMore={issueMorePasses} />}
      </StepContent>}

      <ActionErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
    </div>
  )
}

const InstallPanel = ({ url, buttonImage, label }) => {
  return (
    <div className='flex flex-col gap-4 items-center'>
      <div><Link to={url} target='_pass'><img src={buttonImage} width={250} /></Link></div>
      <div className='bg-white p-4 rounded'>
        <QRCode value={url} />
      </div>
      <div>
        <CopyToClipboard text={url ?? ""} onCopy={() => enqueueSnackbar({ message: `${label} copied`, variant: 'success' })}>
          <button type='button' className='link'>Copy {label}</button>
        </CopyToClipboard>
      </div>
    </div>
  )
}

const UploadDataFileInput = ({ onFile }) => {
  const inputRef = useRef(null)

  const selectFile = file => {
    if (file) onFile(file)
  }

  return (
    <div
      role='button'
      tabIndex={0}
      className='inline-block cursor-pointer'
      onClick={() => inputRef.current?.click()}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={event => event.preventDefault()}
      onDrop={event => {
        event.preventDefault()
        selectFile(event.dataTransfer.files?.[0])
      }}
    >
      <UploadDropTarget />
      <input
        ref={inputRef}
        type='file'
        accept='.json,.csv,.xlsx,application/json,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        className='hidden'
        onClick={event => event.stopPropagation()}
        onChange={event => {
          selectFile(event.target.files?.[0])
          event.target.value = ''
        }}
      />
    </div>
  )
}

const UploadDropTarget = () => {
  return (
    <div
      style={{
        borderRadius: 10,
        border: '2px dashed currentColor',
        backgroundColor: 'transparent',
        color: 'inherit',
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 12,
        paddingBottom: 12,
        minWidth: 320
      }}
    >
      <div className='flex flex-row items-center gap-3'>
        <UploadFileOutlinedIcon />
        <div>
          <div className='text-base font-semibold'>Upload data file</div>
          <div className='opacity-70' style={{ fontSize: 13 }}>Click to choose a file, or drag one here</div>
          <div className='opacity-70' style={{ fontSize: 12 }}>[json, csv, xlsx]</div>
        </div>
      </div>
    </div>
  )
}


const barcodeOptions = {
  bcid: 'qrcode',
  scale: 2,
  includetext: false,
  eclevel: "M",
  ecaddchars: 3,
  layers: 8,
  width: 60,
  height: 60,
  padding: 8,
  backgroundcolor: "#ffffff"
}


const StampcardSignup = ({ template, mode = 'test', onCreated }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)

  const [stampCard, setStampCard] = useState()
  const [barcode, setBarcode] = useState(null)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})

  useEffect(() => {
    let canvas = document.createElement('canvas')

    BwipJs.toCanvas(canvas, { ...barcodeOptions, text: `https://signup.smartix.uk/stamp/${template.client_id}/${template.template_id}` })

    let barcode = canvas.toDataURL('image/svg')

    setBarcode(barcode)
  }, [])

  const makeTestStampcard = async () => {
    setStampCard(null)
    setProcessing(true)
    const sc = await createTestStampcard({ apiSource, apiKey, apiMode: "test", templateId: template.template_id })
    setProcessing(false)

    if (sc.ok === false) {
      setMakePassError(sc.error)
      setErrorDialogOpen(true)
    } else {
      setStampCard(sc)
      onCreated?.(sc)
    }
  }

  const gotoSignupForm = () => {
    window.open(`https://signup.smartix.uk/stamp/${template.client_id}/${template.template_id}`, '_blank', 'noopener')
  }

  return (
    <>
      <div className='m-10 flex flex-col gap-10'>
        <div className='m-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1fr)_auto]'>

          {!(mode === 'live' && !stampCard) && <div>
            <div className='flex flex-col gap-10 p-5 items-center'>
              {!stampCard && mode === 'test' &&
                <>
                  <div className='mt-40'>You can create a test stampcard here.</div>
                  <div><Button variant="contained" onClick={makeTestStampcard}>Create a TEST Stampcard</Button></div>
                </>
              }

              {stampCard &&
                <>
                  <Typography variant='h4'>
                    <CopyToClipboard text={stampCard.pass_id} onCopy={() => enqueueSnackbar({ message: 'Pass ID copied', variant: 'success' })}>
                      <div className='text-nowrap  cursor-pointer'><ContentCopyIcon color='inherit' className="mr-5" />New Stampcard ID: {stampCard.pass_id}</div>
                    </CopyToClipboard>
                  </Typography>
                  <div className='flex gap-40 items-center m-10'>

                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={stampCard.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={stampCard.appleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={stampCard?.appleUrl} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>

                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={stampCard.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={stampCard.googleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={stampCard?.googleUrl} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>

                  </div>
                  <div className='flex flex-row justify-end w-full'>
                    <Button variant='outlined' onClick={() => setStampCard(null)}>close</Button>
                  </div>
                </>
              }
            </div>
          </div>}

          {!stampCard && mode === 'live' &&
            <>
              <div className='flex flex-col gap-3 p-5'>
                <div>If you wish to create live stampcard, then you must use the stampcard signup form </div>
                <div><Button variant="contained" onClick={gotoSignupForm}>Go to live signup form</Button></div>
                <div>Scan for link to signup form</div>
                <div><a target="_blank" rel="noreferrer" href={`https://signup.smartix.uk/stamp/${template.client_id}/${template.template_id}`}>https://signup.smartix.uk/stamp/{template.client_id}/{template.template_id}</a></div>
              </div>
              <div className='bg-white p-5'>
                <img src={barcode} alt="Stampcard signup QR code" />
              </div>
            </>
          }
        </div>
      </div>

      <ActionErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />

    </>
  )
}


const LoyaltycardSignup = ({ template, mode = 'test', onCreated }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)

  const [loyaltyCard, setLoyaltyCard] = useState()
  const [barcode, setBarcode] = useState(null)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})

  useEffect(() => {
    let canvas = document.createElement('canvas')

    BwipJs.toCanvas(canvas, { ...barcodeOptions, text: `https://signup.smartix.uk/loyalty/${template.client_id}/${template.template_id}` })

    let barcode = canvas.toDataURL('image/svg')

    setBarcode(barcode)
  }, [])

  const makeTestLoyaltycard = async () => {
    setLoyaltyCard(null)
    setProcessing(true)
    const sc = await createTestLoyaltycard({ apiSource, apiKey, apiMode: "test", templateId: template.template_id })
    setProcessing(false)

    if (sc.ok === false) {
      setMakePassError(sc.error)
      setErrorDialogOpen(true)
    } else {
      setLoyaltyCard(sc)
      onCreated?.(sc)
    }
  }

  const gotoSignupForm = () => {
    window.open(`https://signup.smartix.uk/loyalty/${template.client_id}/${template.template_id}`, '_blank', 'noopener')
  }

  return (
    <>
      <div className='m-10 flex flex-col gap-10'>
        <div className='m-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1fr)_auto]'>

          {!(mode === 'live' && !loyaltyCard) && <div className='flex flex-col gap-10 p-5 items-center'>
            {!loyaltyCard && mode === 'test' &&
              <>
                <Typography align='center'>You can use the button below to issue a test pass.</Typography>
                <div><Button variant="contained" onClick={makeTestLoyaltycard}>Create a TEST Loyalty card</Button></div>
              </>
            }

              {loyaltyCard &&
                <>
                  <Typography variant='h4'>
                    <CopyToClipboard text={loyaltyCard.pass_id} onCopy={() => enqueueSnackbar({ message: 'Pass ID copied', variant: 'success' })}>
                      <div className='text-nowrap  cursor-pointer'><ContentCopyIcon color='inherit' className="mr-5" />New Loyalty card ID: {loyaltyCard.pass_id}</div>
                    </CopyToClipboard>
                  </Typography>
                  <div className='flex gap-40 items-center m-10'>


                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={loyaltyCard.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={loyaltyCard.appleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={loyaltyCard?.appleUrl} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>

                    <div className='flex flex-col gap-10 items-start'>
                      <div style={{ height: 100 }} className="flex flex-col justify-around">
                        <Link to={loyaltyCard.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link>
                      </div>

                      <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          value={loyaltyCard.googleUrl}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <CopyToClipboard text={loyaltyCard?.googleUrl} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
                        <div className='text-nowrap'><button className='link'><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</button></div>
                      </CopyToClipboard>
                    </div>
                  </div>
                  <div className='flex flex-row justify-end w-full'>
                    <Button variant='outlined' onClick={() => setLoyaltyCard(null)}>close</Button>
                  </div>
                </>
              }
          </div>}

          {!loyaltyCard && mode === 'live' &&
            <>
              <div className='flex flex-col gap-3 p-5'>
                <Typography variant='h6' sx={{ maxWidth: 1100 }}>
                  Managed loyalty cards are usually self-issued by a customer scanning a QR code that you display in your premises.
                  <br />Scan the QR code to visit the self-signup page to issue live passes.
                </Typography>
                <div><Button variant="contained" onClick={gotoSignupForm}>Go to live signup form</Button></div>
                <div>Scan for link to signup form</div>
                <div><a target="_blank" rel="noreferrer" href={`https://signup.smartix.uk/loyalty/${template.client_id}/${template.template_id}`}>https://signup.smartix.uk/loyalty/{template.client_id}/{template.template_id}</a></div>
              </div>
              <div className='bg-white p-5'>
                <img src={barcode} alt="Loyalty card signup QR code" />
              </div>
            </>
          }
        </div>
      </div>

      <ActionErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />

    </>
  )
}



const Giftcard = ({ template, mode = 'test', onCreated }) => {

  const { apiSource, apiKey, setProcessing,copyingEmail, setCopyingEmail } = useContext(AppContext)

  const [giftCard, setGiftCard] = useState(null)
  const [emailBarcodes, setEmailBarcodes] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState({})

  const navigate = useNavigate()

  const copyEmail = () => {
    setCopyingEmail(true)
  }

  const copyEmailBarcodes = () => {
    setEmailBarcodes(true)
    setCopyingEmail(true)
  }

  useEffect(() => {
    if (copyingEmail) {
      document.execCommand("SelectAll", true)
      document.execCommand("Copy", true)
      document.execCommand("UnSelect", true)

      setCopyingEmail(false)
      setEmailBarcodes(false)
    }
  }, [copyingEmail])

  const makeTestGiftcard = async () => {
    setGiftCard(null)
    setProcessing(true)
    const gc = await createGiftcard({ apiSource, apiKey, apiMode: "test", templateId: template.template_id })
    setProcessing(false)

    if (gc.ok === false) {
      setMakePassError(gc.error)
      setErrorDialogOpen(true)
    } else {
      setGiftCard(gc)
      onCreated?.(gc)
    }
  }

  if (copyingEmail) return (
    <div style={{ backgroundColor: "#fff", width: "100%" }}>
      <br />
      <table cellPadding={20} cellSpacing={20}>
        <tbody>
          <tr style={{ height: 100 }}>
            <td><Link to={giftCard?.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link></td>
            <td><Link to={giftCard?.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link></td>
          </tr>
          {emailBarcodes &&
            <tr style={{ height: 200, width: 200 }}>
              <td >
                <div className='bg-white p-5'>
                  <QRCode
                    size={256}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    value={giftCard?.appleUrl}
                    viewBox={`0 0 256 256`}
                  />
                </div>
              </td>

              <td >
                <div className='bg-white p-5'>
                  <QRCode
                    size={256}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    value={giftCard?.googleUrl}
                    viewBox={`0 0 256 256`} />
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
      <br /><br />
    </div>
  )

  return (
    <>
      <div className='m-10 flex flex-col gap-10 h-full overflow-y-auto'>
        {!giftCard &&
          <div className='m-10 flex flex-row gap-10 justify-around'>

            {mode === 'test' && <div>
              <div className='flex flex-col gap-3 p-5'>
                <div className='mt-28'>You can create a test giftcard here.</div>
                <Button variant="contained" onClick={makeTestGiftcard}>Create a TEST Giftcard</Button>
              </div>
            </div>}

            {mode === 'live' && <div>
              <div className='flex flex-col gap-3 p-5'>
                <div className='flex flex-col gap-3 items-start'><Typography variant="h5" sx={{ p: 2 }}>Create a Giftcard</Typography></div>
                <CreateLiveGiftcardForm template={template} setGiftCard={setGiftCard} onCreated={onCreated} />
              </div>
            </div>}

          </div>
        }

        {giftCard &&
          <div className='flex flex-row gap-40 items-center m-10'>

            <div>
              <div className='flex flex-row gap-20 p-5'>
                <div className='flex flex-col gap-10 items-start'>
                  <div style={{ height: 100 }} className="flex flex-col justify-around">
                    <Link to={giftCard.appleUrl} target='_pass'><img src={appleButton} width={250} /></Link>
                  </div>

                  <div style={{ height: 250, width: 250 }} className='bg-white p-5' >
                    <QRCode
                      size={256}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      value={giftCard.appleUrl}
                      viewBox={`0 0 256 256`}
                    />
                  </div>

                  <CopyToClipboard text={giftCard?.appleUrl} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
                    <Button><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</Button>
                  </CopyToClipboard>
                </div>

                <div className='flex flex-col gap-10 items-start'>
                  <div style={{ height: 100 }} className="flex flex-col justify-around">
                    <Link to={giftCard.googleUrl} target='_pass'><img src={googleButton} width={250} /></Link>
                  </div>

                  <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
                    <QRCode
                      size={256}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      value={giftCard.googleUrl}
                      viewBox={`0 0 256 256`}
                    />
                  </div>

                  <CopyToClipboard text={giftCard?.googleUrl} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
                    <Button><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</Button>
                  </CopyToClipboard>
                </div>
              </div>
            </div>

            <div>
              <div className='flex flex-col gap-5 p-5'>
                <Typography variant='h6'>Gift card ID:
                  <CopyToClipboard text={giftCard.pass_id} onCopy={() => enqueueSnackbar({ message: 'Gift card ID copied', variant: 'success' })} options={{ format: 'text/plain' }}>
                    <Button ><ContentCopyIcon color='inherit' className="mr-5" />{giftCard.pass_id}</Button>
                  </CopyToClipboard>
                </Typography>

                <Button variant='contained' color="secondary" onClick={copyEmailBarcodes}><ContentCopyIcon color='inherit' className="mr-5" />Copy email content (with barcodes)</Button>
                <Button variant='contained' color="secondary" onClick={copyEmail}><ContentCopyIcon color='inherit' className="mr-5" />Copy email content (no barcodes)</Button>

                <Button variant='contained' color="secondary" onClick={() => navigate("../passes/" + giftCard.pass_id)}>Goto pass details</Button>
                <br />
                <Button variant='contained' onClick={() => setGiftCard(null)}>Close</Button>
              </div>
            </div>

          </div>
        }

      </div>

      <ActionErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
    </>
  )
}


const CreateLiveGiftcardForm = ({ template, setGiftCard, onCreated }) => {
  const { apiSource, apiKey, setProcessing } = useContext(AppContext)

  const defaultExpiry = moment().add(1, "year")

  const [amount, setAmount] = useState("")
  const [hasExpiry, setHasExpiry] = useState("yes")
  const [expiry, setExpiry] = useState(defaultExpiry)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [makePassError, setMakePassError] = useState()

  const regex = /^(\d+(?:[\.]\d{0,2})?)$/i

  const makeGiftcard = async () => {
    setGiftCard(null)
    setProcessing(true)

    const gc = await createGiftcard({ apiSource, apiKey, apiMode: "live", templateId: template.template_id, amount: parseInt(amount, 10), expiry: hasExpiry === "yes" ? expiry.format("YYYY-MM-DDTHH:mm:ssZ") : undefined })

    setProcessing(false)

    if (gc.ok === false) {
      setMakePassError(gc.error)
      setErrorDialogOpen(true)
    } else {
      setGiftCard(gc)
      onCreated?.(gc)
    }
  }

  return (
    <>
      <div>

        <TextField
          sx={{ m: 1, width: '25ch' }}
          value={amount}
          onChange={e => {
            if (e.target.value === '' || regex.test(e.target.value)) {
              setAmount(e.target.value)
            }
          }}
          variant='outlined'
          label="Amount"
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><div style={{ fontSize: 40, color: "#999" }}>£</div></InputAdornment>,
              style: { fontSize: 40, width: 235 }
            },
            inputLabel: {
              shrink: true
            }
          }}
        />

      </div>
      <RadioGroup
        row
        value={hasExpiry}
        onChange={e => setHasExpiry(e.target.value)}
      >
        <FormControlLabel value="yes" control={<Radio />} label="Set an expiry date" />
        <FormControlLabel value="no" control={<Radio />} label="No expiry date" />
      </RadioGroup>


      <div><DatePicker label="Expiry Date" value={expiry} onChange={date => setExpiry(date)} disabled={hasExpiry === "no"} /></div>

      <div><Button onClick={makeGiftcard} variant='contained' disabled={amount < 1} >Create Giftcard</Button></div>

      <ActionErrorDialog open={errorDialogOpen} setOpen={setErrorDialogOpen} error={makePassError} />
    </>
  )
}
